import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { service } from '../lib/api/service'
import type { Project, Surcharge, CombinedEntry } from '../lib/api/types'
import NotificationPaper from './printSections/NotificationPaper'
import ATASpecPaper from './printSections/ATASpecPaper'
import ATASummaryPaper from './printSections/ATASummaryPaper'

/** Combined print (#12356): stacks the chosen sections from ?inc=notification,spec,ata
 *  onto one print job, each on its own A4 page. */
export default function CombinedPrint() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sortOrder = parseInt(id ?? '', 10)

  const inc = new Set((searchParams.get('inc') ?? '').split(',').filter(Boolean))

  const [data, setData]         = useState<CombinedEntry | null>(null)
  const [project, setProject]   = useState<Project | null>(null)
  const [surcharges, setSurcharges] = useState<Surcharge[]>([])
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    Promise.all([service.getProject(), service.getAllEntries(), service.getSurcharges()])
      .then(([proj, entries, sc]) => {
        setProject(proj)
        setData(entries.find(e => e.sortOrder === sortOrder) ?? null)
        setSurcharges(sc)
      })
      .finally(() => setLoading(false))
  }, [sortOrder])

  const doPrint = () => {
    document.body.dataset.printing = '1'
    window.print()
    delete document.body.dataset.printing
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const nr = `ÄTA-${String(sortOrder).padStart(3, '0')}`
  // Render in canonical packet order regardless of the ?inc ordering.
  const showSummary = inc.has('ata')
  const showNotification = inc.has('notification') && !!data?.notification
  const showSpec = inc.has('spec') && !!data?.ataSpec
  const nothing = !showSummary && !showNotification && !showSpec

  return (
    <div className="print-overlay">
      <div className="print-toolbar no-print">
        <button className="icon-btn" onClick={() => navigate(`/ata/${sortOrder}`)}>
          <ArrowLeft size={14} /> Tillbaka
        </button>
        <span style={{ fontSize: 13, color: 'var(--ink-dim)' }}>
          Utskrift · {nr}
        </span>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={doPrint} disabled={nothing}>
          <Printer size={14} /> Skriv ut
        </button>
      </div>

      {nothing ? (
        <div className="loading">
          <p style={{ color: 'var(--ink-faint)' }}>Inget valt att skriva ut för {nr}.</p>
        </div>
      ) : (
        <>
          {showSummary && (
            <ATASummaryPaper
              journal={data?.journal ?? null}
              notification={data?.notification ?? null}
              ataSpec={data?.ataSpec ?? null}
              project={project}
              surcharges={surcharges}
              sortOrder={sortOrder}
            />
          )}
          {showNotification && data?.notification && (
            <NotificationPaper
              journal={data.journal}
              notification={data.notification}
              project={project}
              sortOrder={sortOrder}
            />
          )}
          {showSpec && data?.ataSpec && (
            <ATASpecPaper
              journal={data.journal}
              ataSpec={data.ataSpec}
              project={project}
              surcharges={surcharges}
              sortOrder={sortOrder}
            />
          )}
        </>
      )}
    </div>
  )
}
